import { InjectionToken } from '@angular/core';

/**
 * One user-editable field of a form, as tracked for amendment indicators.
 *
 * Widget ids are regenerated on every load, so fields are identified by position instead:
 * `s<section>:w<widget>` for a widget, with a field key appended when a widget holds several
 * fields (a column index, a named control, or a row index plus key). The same form structure
 * yields the same ids on every version.
 */
export interface AmendmentField {
  /**
   * Stable, position-based id stored in `changed_field_ids`.
   */
  readonly id: string;

  /**
   * Normalized value; two versions with equal values did not amend this field.
   */
  readonly value: unknown;
}

/**
 * A field as reported by a widget-level extractor, before the widget position is prepended.
 */
export interface AmendmentWidgetField {
  /**
   * Key of the field inside the widget, or null when the widget is a single field.
   */
  readonly fieldKey: string | null;

  /**
   * Normalized value of the field.
   */
  readonly value: unknown;
}

/**
 * Reads the trackable fields of one widget from its `properties`.
 */
export type WidgetFieldExtractor = (properties: Record<string, unknown>) => AmendmentWidgetField[];

/**
 * The widget a field indicator belongs to. Provided by the form element that hosts each widget
 * component, so indicators placed anywhere in a widget's template tree can find their widget.
 */
export interface AmendmentWidgetHost {
  readonly payload: object | null | undefined;
}

export const AMENDMENT_WIDGET_HOST = new InjectionToken<AmendmentWidgetHost>(
  'AMENDMENT_WIDGET_HOST',
);

const WIDGET_ID_PATTERN = /^(s\d+:w\d+)(?::(.+))?$/;

/**
 * Builds the id of a widget from its position in the form.
 */
export function buildAmendmentWidgetId(sectionIndex: number, widgetIndex: number): string {
  return `s${sectionIndex}:w${widgetIndex}`;
}

/**
 * Builds the id of a field inside a widget.
 */
export function buildAmendmentFieldId(widgetId: string, fieldKey: string | null): string {
  return fieldKey === null ? widgetId : `${widgetId}:${fieldKey}`;
}

/**
 * Builds the key of a field that lives in a repeatable row of a widget.
 */
export function buildAmendmentRowFieldKey(rowIndex: number, fieldKey?: string | null): string {
  return fieldKey ? `r${rowIndex}:${fieldKey}` : `r${rowIndex}`;
}

/**
 * Splits a field id into its widget id and field key (null for a whole-widget field).
 */
export function parseAmendmentFieldId(
  id: string,
): { widgetId: string; fieldKey: string | null } | null {
  const match = WIDGET_ID_PATTERN.exec(id);
  return match ? { widgetId: match[1], fieldKey: match[2] ?? null } : null;
}
